import { useState } from "react";
import { useToast } from "@/components/ui/use-toast";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { format } from "date-fns";
import { PaymentStatusBadge } from "./PaymentStatusBadge";
import { PaymentDetailsDialog } from "./table/PaymentDetailsDialog";
import type { Payment } from "./table/types";
import { Loader2, FileText, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

interface PaymentHistoryTableProps {
  payments: Payment[];
  isLoading: boolean;
}

export function PaymentHistoryTable({
  payments,
  isLoading,
}: PaymentHistoryTableProps) {
  const [selectedPayment, setSelectedPayment] = useState<Payment | null>(null);
  const { toast } = useToast();

  const formatDescription = (payment: Payment) => {
    if (!payment.tax_return_requests) return "Payment";
    const { service_type, financial_year } = payment.tax_return_requests;
    return `${service_type} - FY${financial_year}`;
  };

  // Loading state with skeleton
  if (isLoading) {
    return (
      <div className="rounded-md border bg-card">
        <div className="p-8 flex flex-col items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary mb-4" />
          <p className="text-muted-foreground">Loading payment history...</p>
        </div>
      </div>
    );
  }

  // Empty state
  if (payments.length === 0) {
    return (
      <div className="rounded-md border bg-card">
        <div className="p-8 flex flex-col items-center justify-center text-center">
          <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center mb-4">
            <FileText className="h-6 w-6 text-primary" />
          </div>
          <h3 className="text-lg font-medium mb-2">No payments found</h3>
          <p className="text-muted-foreground max-w-md mb-4">
            We couldn't find any payments matching your search criteria. Try
            adjusting your filters or check back later.
          </p>
          <Button variant="outline" onClick={() => window.location.reload()}>
            Refresh
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-md border bg-card overflow-hidden">
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-muted/50">
              <TableHead className="w-[120px]">Date</TableHead>
              <TableHead>Description</TableHead>
              <TableHead>Amount</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="hidden md:table-cell">Reference</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {payments.map((payment) => (
              <TableRow
                key={payment.id}
                className="hover:bg-muted/50 cursor-pointer transition-colors"
                onClick={() => setSelectedPayment(payment)}
              >
                <TableCell className="font-medium">
                  {format(new Date(payment.created_at), "PPP")}
                </TableCell>
                <TableCell>{formatDescription(payment)}</TableCell>
                <TableCell className="font-medium">
                  ${payment.amount.toFixed(2)} AUD
                </TableCell>
                <TableCell>
                  <PaymentStatusBadge status={payment.status} />
                </TableCell>
                <TableCell className="font-mono text-xs text-muted-foreground hidden md:table-cell">
                  {payment.id}
                </TableCell>
                <TableCell className="text-right">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 text-primary hover:text-primary/80"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedPayment(payment);
                    }}
                  >
                    View Details
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {selectedPayment && (
        <PaymentDetailsDialog
          payment={selectedPayment}
          isOpen={!!selectedPayment}
          onClose={() => setSelectedPayment(null)}
        />
      )}
    </div>
  );
}
